import { useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  PencilIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  PaperClipIcon,
  ArrowUpTrayIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Card,
  CardHeader,
  Badge,
  getStatusBadgeVariant,
  ContentLoader,
  EntityLink,
  BackButton,
  Input,
  Select,
} from '@/components/common';
// Imported by path rather than through components/common/index.ts, which is
// outside this task's file domain.
import { RecordTimestamps } from '@/components/common/RecordTimestamps';
import { QuotePremiumOptionsCard } from './QuotePremiumOptionsCard';
import { QuotePricingRequestsCard } from './QuotePricingRequestsCard';
import { QuoteCoverDetailsCard, type QuoteHealthData, type QuoteTravelData } from './QuoteCoverDetailsCard';
import type { VehicleCategory } from './wizard/steps/common/PremiumOptionsStep';
import { useQuote, useSubmitQuote } from '@/hooks';
import { quoteService, getErrorMessage } from '@/services';
import type { QuoteDocument } from '@/services/quotes';
import { useAuthStore } from '@/store';
import { useCan } from '@/hooks/useEmployeeRights';
import { formatDate, formatDateTime, formatRelativeTime, titleCase } from '@/utils';
import { QUOTE_STATUS_OPTIONS, quoteStatusLabel } from './quoteStatus';

// The status vocabulary and its labels now live in `./quoteStatus`, shared with
// QuotesListPage's filter -- the two used to disagree, and the filter offered
// only six of the eleven values this control has always listed.
const statusLabel = quoteStatusLabel;

/** The identifiers the customer submits, labelled for a human reviewer.
 *  Which of them are REQUIRED depends on the proposer type and is decided by
 *  the server (`kyc.required_fields`); this map only names what came back. */
const KYC_FIELD_LABELS: Record<string, string> = {
  aadhaar_number: 'Aadhaar Number',
  pan_number: 'PAN',
  gst_number: 'GST Number',
};

const PAYMENT_MODE_OPTIONS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
];

/** D3's review block on the detail payload. Declared locally rather than in
 *  `src/types/index.ts`, which is outside this task's file domain -- same
 *  approach as `previous_insurer_name` below. */
/**
 * The two blocks of `QuoteRequest.additional_data` this page reads. BOARD #278.
 *
 * Declared here rather than in `src/types/index.ts` because that file is frozen
 * for this batch, and READ-ONLY on purpose: nothing on this page writes
 * `additional_data`, and every field is optional because these blocks are
 * written by three different forms (the wizard, the public site, the vehicle
 * lookup) and an older row will be missing most of them.
 */
interface QuoteVehicleData {
  /** The wizard's vehicle category (`two_wheeler` | `private_car` |
   *  `commercial_vehicle`); anything else is ignored. B14. */
  vehicle_type?: string;
  registration_number?: string;
  make?: string;
  model?: string;
  variant?: string;
  fuel_type?: string;
  manufacturing_year?: number | null;
  registration_date?: string | null;
  rto_code?: string;
  engine_number?: string;
  chassis_number?: string;
  idv?: string | null;
  previous_insurer?: string;
  is_financed?: boolean;
  financier_name?: string;
}

/**
 * B14. The stored vehicle type, narrowed to the three the Product dropdown can
 * filter by. An unknown or missing value gives `undefined`, which leaves the
 * add-option dropdown unfiltered -- the same as any non-motor quote, and the
 * same scope the API guard applies (it ignores unrecognised values too).
 */
function quoteVehicleCategory(raw: string | undefined): VehicleCategory | undefined {
  return raw === 'two_wheeler' || raw === 'private_car' || raw === 'commercial_vehicle' ? raw : undefined;
}

/**
 * Where a publicly originated quote came from.
 *
 * `phone_verified` is rendered AS THE FALSE IT IS. No delivery channel exists to
 * send a one-time code (board #259), so nobody on this platform has verified
 * that number -- and a broker about to ring a stranger needs to know that, not
 * be shown a contact that looks checked. It is evidence for a human, never an
 * authorisation input.
 */
interface QuotePublicOrigin {
  channel?: string;
  lead_id?: string;
  lead_number?: string;
  originated_at?: string;
  site_slug?: string;
  phone_verified?: boolean;
}

interface QuoteExtraData {
  vehicle_data?: QuoteVehicleData;
  health_data?: QuoteHealthData;
  travel_data?: QuoteTravelData;
  public_origin?: QuotePublicOrigin;
  address?: string;
  [key: string]: unknown;
}

interface KycReviewBlock {
  required_fields: string[];
  identifiers: Record<string, string>;
  submitted: boolean;
  submitted_at: string | null;
  awaiting_review: boolean;
  reviewed_at: string | null;
  rejection_reason: string;
}

export function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data: quote, isLoading, error } = useQuote(id!);
  const submitMutation = useSubmitQuote();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  // B4: every broker-side control on this screen (KYC review, payment, policy
  // upload, share, convert) is a quotes Edit on the API.
  const canEditQuote = useCan('quotes', 'edit');
  // B4: links go only where the destination route will admit the user --
  // Convert opens /policies/new (policies Create), Duplicate /quotes/new
  // (quotes Create).
  const canCreatePolicy = useCan('policies', 'create');
  const canCreateQuote = useCan('quotes', 'create');
  const isBrokerAdmin = (user?.role === 'broker_admin' || user?.role === 'broker_employee') && canEditQuote;

  // Attachments the customer sees on the shared link. Only quote_pdf and
  // comparison_pdf are ever public -- the server filters the rest out, so
  // uploading anything else here simply will not appear there.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const policyFileInputRef = useRef<HTMLInputElement>(null);
  const [docType, setDocType] = useState('quote_pdf');
  const [rejectionReason, setRejectionReason] = useState('');
  const [payment, setPayment] = useState({
    payment_reference: '',
    payment_date: '',
    payment_mode: 'bank_transfer',
  });
  const { data: documents } = useQuery({
    queryKey: ['quoteDocuments', id],
    queryFn: () => quoteService.getQuoteDocuments(id!),
    enabled: !!id,
  });

  const uploadDoc = useMutation({
    mutationFn: (formData: FormData) => quoteService.uploadQuoteDocument(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quoteDocuments', id] });
      // The issued policy upload also COMPLETES the quote server-side, so the
      // quote itself has to be re-read or the page keeps showing the old status
      // and its now-stale panels.
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
      toast.success('Attachment uploaded');
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (policyFileInputRef.current) policyFileInputRef.current.value = '';
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteDoc = useMutation({
    mutationFn: (documentId: string) => quoteService.deleteQuoteDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quoteDocuments', id] });
      toast.success('Attachment removed');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const uploadFile = (file: File, documentType: string) => {
    if (!id) return;
    const formData = new FormData();
    formData.append('quote_request', id);
    formData.append('document_type', documentType);
    formData.append('file', file);
    uploadDoc.mutate(formData);
  };

  const handleFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file, docType);
  };

  const handlePolicyFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file, 'policy_document');
  };

  /** Fetch through the API client, not a bare href: the download endpoint is
   *  JWT-authenticated, so a plain <a> would arrive unauthenticated and 401. */
  const downloadDoc = async (doc: QuoteDocument) => {
    try {
      const blob = await quoteService.downloadQuoteDocument(doc.id);
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = doc.name || 'quote-document';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const approveKyc = useMutation({
    mutationFn: () => quoteService.approveQuoteKyc(id!),
    onSuccess: () => {
      toast.success('KYC approved — you can now record the payment and upload the policy');
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const rejectKyc = useMutation({
    mutationFn: (reason: string) => quoteService.rejectQuoteKyc(id!, reason),
    onSuccess: () => {
      toast.success('KYC rejected — the customer has been asked to send new documents');
      setRejectionReason('');
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const recordPayment = useMutation({
    mutationFn: () => quoteService.recordQuotePayment(id!, payment),
    onSuccess: () => {
      toast.success('Payment recorded');
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const updateStatus = useMutation({
    mutationFn: (status: string) => quoteService.updateQuoteStatus(id!, status),
    onSuccess: () => {
      toast.success('Quote status updated');
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  // SHARING LIVES ON THE PREMIUM OPTIONS CARD, and only there.
  //
  // This screen used to carry two controls for it: an honest one here
  // ("Create Public Link" -> the URL, the clipboard, and the two QUOTES-5
  // channel buttons) and a second on the options card that called the same
  // `share` endpoint, discarded the URL it returned, and toasted "Quote sent".
  // Because the URL never reached `publicUrl`, pressing the card's button left
  // THIS panel hidden as well -- the broker was told the customer had the quote
  // and was given neither the link nor either send button.
  //
  // One control now, on the card, beside the options being sent. It keeps the
  // SAME audience: `canShare={isBrokerAdmin}` below, which is the rule this
  // sidebar already applied and the one `share_via` enforces server-side
  // ("Broker staff only", views.py). The card used to show its send button to
  // POSPs and insurer logins as well; that is tightened by the same prop.
  // See SharePanel in QuotePremiumOptionsCard.

  const handleSubmit = () => {
    submitMutation.mutate(id!);
  };

  if (isLoading) {
    return <ContentLoader />;
  }

  if (error || !quote) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Quote not found</h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2">The quote you are looking for does not exist.</p>
        <Link to="/quotes" className="mt-4 inline-block">
          <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
            Back to Quotes
          </Button>
        </Link>
      </div>
    );
  }

  // Issued-policy threading (round-2 Wave F3, item 10): a QuoteRequest can
  // have several per-insurer Quote rows (`quotes`, nested by
  // QuoteRequestDetailSerializer); at most one of those is ever converted
  // into a Policy. Surface that one, if any, as "issued as policy <#>".
  const issuedQuote = quote.quotes?.find((q) => q.issued_policy_number);

  // QuoteRequestDetailSerializer resolves the FK to `previous_insurer_name`;
  // the shared `Quote` type in src/types does not declare it yet (that file is
  // outside this change's file domain), hence the local widening.
  const previousInsurerName =
    (quote as { previous_insurer_name?: string | null }).previous_insurer_name ?? '';

  // Same widening, same reason: QuoteRequestDetailSerializer sends the
  // proposer's phone and email (serializers.py), the shared `Quote` type does
  // not declare them. These decide whether each Share button can do anything.
  const proposerContact = quote as { proposer_phone?: string | null; proposer_email?: string | null };
  // NCB is an IntegerField percentage, so a genuine 0% NCB is falsy — `||`
  // turned it into a dash.
  const previousNcb =
    quote.previous_ncb === null || quote.previous_ncb === undefined || quote.previous_ncb === ''
      ? '-'
      : `${quote.previous_ncb}%`;

  const kyc = (quote as { kyc?: KycReviewBlock }).kyc;

  // BOARD #278. The vehicle and where the quote came from -- neither was on
  // this page at all. `QuoteRequestDetailSerializer` has always sent every one
  // of these (the columns, plus `additional_data`), so a broker looking at a
  // motor quote could not see the registration number, the variant, the RTO,
  // the IDV, or that the quote had been raised by a stranger on their public
  // website rather than by their own staff.
  //
  // Narrowed LOCALLY rather than in `src/types/index.ts`, which is frozen for
  // this batch -- the same local widening the four reads above already use.
  const vehicleInfo = quote as {
    registration_number?: string | null;
    registration_date?: string | null;
    manufacturing_year?: number | null;
    vehicle_variant_name?: string | null;
    rto_name?: string | null;
    rto_code?: string | null;
    vehicle_idv?: string | number | null;
    is_vehicle_financed?: boolean;
    financier_name?: string | null;
    cover_type?: string | null;
    additional_data?: QuoteExtraData;
  };
  const extra = vehicleInfo.additional_data ?? {};
  const vehicleData = extra.vehicle_data ?? {};
  const publicOrigin = extra.public_origin;
  const hasVehicle = Boolean(
    vehicleInfo.registration_number ||
      vehicleInfo.vehicle_variant_name ||
      vehicleData.variant ||
      vehicleData.make ||
      vehicleInfo.vehicle_idv
  );
  const vehicleDescription =
    vehicleInfo.vehicle_variant_name ||
    [vehicleData.make, vehicleData.model, vehicleData.variant].filter(Boolean).join(' ') ||
    '';

  // Same local widening as `proposerContact` above: QuoteRequestDetailSerializer
  // sends `is_shared`/`shared_at` (they are what decide whether the customer's
  // link resolves at all), the shared `Quote` type in src/types does not declare
  // them, and that file is outside this task's file domain.
  const sharing = quote as { is_shared?: boolean; shared_at?: string | null };

  // One document list, three audiences. The card below says "shown to the
  // customer on the shared link" and used to list every row on the quote --
  // including the KYC scans, which are NOT public. Splitting them keeps that
  // promise true and gives the reviewer and the issued policy their own places.
  const allDocuments = documents?.results ?? [];
  const attachments = allDocuments.filter(
    (doc) => doc.document_type === 'quote_pdf' || doc.document_type === 'comparison_pdf'
  );
  const kycDocuments = allDocuments.filter((doc) => doc.document_type === 'kyc_document');
  const policyDocuments = allDocuments.filter((doc) => doc.document_type === 'policy_document');

  // D4. The server refuses payment before `kyc_approved` and refuses the policy
  // upload before it too, so these flags only decide what is DRAWN -- they are
  // not the gate. `completed` keeps the issuance card so a wrong policy copy can
  // still be replaced.
  const canRecordPayment = canEditQuote && quote.status === 'kyc_approved';
  const canUploadPolicy = canEditQuote && (quote.status === 'kyc_approved' || quote.status === 'completed');

  // QUOTES-6. "Convert to Policy" used to appear only at `completed` — but
  // `completed` is reached BY uploading the issued policy, so the button
  // arrived after the work it was meant to start, and the broker had to create
  // the policy some other way to get there. It now appears once the broker has
  // recorded the payment (which the server already refuses before KYC approval,
  // so this cannot jump the gate), and disappears once a policy actually
  // exists — `issuedQuote` is the proof of that, not the status string.
  const paymentRecorded = Boolean(quote.acceptance?.payment_reference);

  // Queue #236: a quote with no linked customer must not be convertible. The
  // Add Policy form it opens prefills the holder FROM the quote, so converting
  // an orphan produces a policy whose holder identity came from nowhere -- and
  // the resulting policy has no Customer to hang renewals, claims or the
  // customer login off.
  //
  // Since #237 the link is written at creation, so this is reachable only for
  // historical rows: production had 12 such quotes, of which the backfill
  // (quotes migration 0012) resolved 7. The 5 it refused all share a test phone
  // or email with a DIFFERENT person's contact, which is exactly the case where
  // guessing would put the wrong name on a policy.
  const hasLinkedCustomer = Boolean(quote.customer_profile_id || quote.customer);
  const canConvertToPolicy =
    canEditQuote && canCreatePolicy && paymentRecorded && !issuedQuote && hasLinkedCustomer;

  return (
    <div>
      <div className="mb-4">
        <BackButton to="/quotes" />
      </div>
      <PageHeader
        title={quote.request_number}
        subtitle={`${quote.insurance_type_name} - ${titleCase(quote.business_type)}`}
        breadcrumbs={[
          { label: 'Quotes', href: '/quotes' },
          { label: quote.request_number },
        ]}
        actions={
          <div className="flex items-center gap-3">
            {quote.status === 'draft' && (
              <>
                <Link to={`/quotes/${id}/edit`}>
                  <Button variant="outline" leftIcon={<PencilIcon className="w-5 h-5" />}>
                    Edit
                  </Button>
                </Link>
                <Button
                  leftIcon={<CheckCircleIcon className="w-5 h-5" />}
                  onClick={handleSubmit}
                  isLoading={submitMutation.isPending}
                >
                  Submit Quote
                </Button>
              </>
            )}
            {canConvertToPolicy && (
              <Link to={`/policies/new?quote=${id}`}>
                <Button leftIcon={<DocumentDuplicateIcon className="w-5 h-5" />}>
                  Convert to Policy
                </Button>
              </Link>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quote Details */}
          <Card>
            <CardHeader title="Quote Details" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <InfoItem label="Quote Number" value={quote.request_number} />
              <InfoItem label="Business Type" value={titleCase(quote.business_type)} />
              <InfoItem label="Insurance Type" value={quote.insurance_type_name} />
              <InfoItem label="Proposer Type" value={titleCase(quote.proposer_type)} />
              <InfoItem label="Broker" value={quote.broker_name} />
              <InfoItem label="POSP" value={quote.posp_name || '-'} />
              {issuedQuote && (
                <InfoItem
                  label="Issued Policy"
                  value={
                    <EntityLink
                      kind="policy"
                      id={issuedQuote.issued_policy_id ?? undefined}
                      name={`Issued as policy ${issuedQuote.issued_policy_number}`}
                    />
                  }
                />
              )}
            </div>
          </Card>

          <QuotePremiumOptionsCard
            requestId={id!}
            insuranceTypeId={quote.insurance_type}
            vehicleCategory={quoteVehicleCategory(vehicleData.vehicle_type)}
            isShared={Boolean(sharing.is_shared)}
            sharedAt={sharing.shared_at ?? null}
            status={quote.status}
            canShare={isBrokerAdmin}
            proposerPhone={proposerContact.proposer_phone}
            proposerEmail={proposerContact.proposer_email}
          />

          {/* #399: ask an insurer's Quote Desk to price this request. */}
          <QuotePricingRequestsCard
            requestId={id!}
            canAct={
              canEditQuote &&
              ['broker_admin', 'broker_employee', 'posp', 'super_posp'].includes(user?.role ?? '')
            }
          />

          {/* Customer Information */}
          <Card>
            <CardHeader title="Customer Information" />
            {/* B2: this was gated on `quote.customer` -- the users.User FK, which
                is NULL for every wizard-created customer because such a customer
                has no login. The card therefore said "no customer linked" on
                precisely the quotes that DO have one. `customer_profile_id` is
                the field that is actually populated (BROKER-6). */}
            {quote.customer_profile_id || quote.customer ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <InfoItem label="Customer Name" value={quote.customer_name || '-'} />
                <InfoItem label="Email" value={quote.customer_email || '-'} />
                <InfoItem label="Phone" value={quote.customer_phone || '-'} />
              </div>
            ) : (
              // BROKER-6: the customer is linked at creation now -- picked from
              // "Select Existing Customer" or created by "Add New Customer" --
              // so the manual "Link Customer" action is gone. This branch is
              // only reachable for historical quotes saved before that change.
              //
              // Queue #236: it used to say only "No customer linked", while the
              // Convert to Policy button sat enabled in the header. Converting
              // from here produced a policy with no customer behind it, so the
              // state needs naming, not just noting -- and the proposer details
              // the quote DOES hold are shown, because they are what somebody
              // needs in order to fix it.
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  No customer linked to this quote
                </p>
                <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                  This quote cannot be converted to a policy until a customer is
                  linked — a policy created from it would have no holder on record.
                </p>
                {(quote.proposer_name || quote.proposer_phone || quote.proposer_email) && (
                  <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                    Quote was raised for{' '}
                    <span className="font-medium">{quote.proposer_name || 'an unnamed proposer'}</span>
                    {quote.proposer_phone ? ` · ${quote.proposer_phone}` : ''}
                    {quote.proposer_email ? ` · ${quote.proposer_email}` : ''}. Create or open that
                    contact, then raise a fresh quote against it.
                  </p>
                )}
              </div>
            )}
          </Card>

          {/* What the customer did on the shared link. Only rendered once they
              have acted, so it stays out of the way on an untouched quote. */}
          {quote.acceptance && (
            <Card>
              <CardHeader
                title="Customer Response"
                // Author-neutral since D4: the same `payment_*` fields are now
                // written either by the customer's own declaration on the public
                // link or by the broker's Record Payment below, and the row does
                // not carry which. The activity log does — that is where "who
                // said so" is answered.
                subtitle={
                  quote.acceptance.payment_declared
                    ? 'A payment reference has been recorded — confirm receipt, then issue and upload the policy.'
                    : 'The customer has chosen an option and no payment has been recorded yet.'
                }
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <InfoItem
                  label="Chosen Option"
                  value={
                    quote.acceptance.insurer_name
                      ? `${quote.acceptance.cover_label ? `${quote.acceptance.cover_label} — ` : ''}${
                          quote.acceptance.insurer_name
                        } (${quote.acceptance.quote_number})`
                      : quote.acceptance.quote_number
                  }
                />
                <InfoItem label="Chosen On" value={formatDate(quote.acceptance.accepted_at)} />
                {quote.acceptance.payment_declared && (
                  <>
                    <InfoItem
                      label="Payment Reference"
                      value={quote.acceptance.payment_reference || '-'}
                    />
                    <InfoItem
                      label="Paid On"
                      value={
                        quote.acceptance.payment_date
                          ? formatDate(quote.acceptance.payment_date)
                          : '-'
                      }
                    />
                    <InfoItem
                      label="Paid By"
                      value={quote.acceptance.payment_mode_display || '-'}
                    />
                    <InfoItem
                      label="Reported On"
                      value={
                        quote.acceptance.payment_declared_at
                          ? formatDate(quote.acceptance.payment_declared_at)
                          : '-'
                      }
                    />
                  </>
                )}
              </div>
              {/* The public link needs no verification, so the accept is only
                  as good as its attribution. Shown to the broker so a disputed
                  acceptance can be investigated rather than argued about. */}
              {quote.acceptance.accepted_ip && (
                <p className="mt-4 text-xs text-gray-400">
                  Accepted from {quote.acceptance.accepted_ip}
                </p>
              )}
            </Card>
          )}

          {/* D3: the KYC review panel. Drawn only while the quote is actually
              awaiting review -- `kyc.awaiting_review` is the SERVER's answer to
              that question, so this panel and the endpoint cannot disagree
              about whether Approve is offered. Hidden from POSPs: selling is
              their job, vouching for identity papers is the broker's, and the
              endpoint refuses them regardless. */}
          {isBrokerAdmin && kyc?.awaiting_review && (
            <Card>
              <CardHeader
                title="KYC Review"
                subtitle="The customer has submitted their documents. Approve them to release the payment link, or reject them with a reason."
              />
              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {Object.entries(kyc.identifiers).map(([field, value]) => (
                    <InfoItem
                      key={field}
                      label={KYC_FIELD_LABELS[field] ?? titleCase(field)}
                      value={value}
                    />
                  ))}
                  {kyc.submitted_at && (
                    <InfoItem label="Submitted On" value={formatDate(kyc.submitted_at)} />
                  )}
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Uploaded Documents</p>
                  {kycDocuments.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      The customer submitted their numbers without attaching a scan. Check the
                      numbers above, or reject and ask for a photo of each document.
                    </p>
                  ) : (
                    <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                      {kycDocuments.map((doc) => (
                        <li key={doc.id} className="py-3 flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <PaperClipIcon className="w-5 h-5 text-gray-400 shrink-0" />
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{doc.name}</p>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => downloadDoc(doc)}>
                            Download
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="border-t border-gray-100 dark:border-gray-700 pt-4 space-y-3">
                  <Button
                    leftIcon={<CheckCircleIcon className="w-5 h-5" />}
                    onClick={() => approveKyc.mutate()}
                    isLoading={approveKyc.isPending}
                  >
                    Approve KYC
                  </Button>

                  <div>
                    <label
                      htmlFor="kyc-rejection-reason"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Reason for rejection
                    </label>
                    <textarea
                      id="kyc-rejection-reason"
                      rows={2}
                      maxLength={500}
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="e.g. The PAN card photo is cut off — please send the whole card."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    {/* The customer reads this on their own link, so it has to
                        say what to fix -- "rejected" with nothing after it is a
                        dead end they cannot answer. The server requires it too. */}
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Shown to the customer on their quote link so they know what to send again.
                    </p>
                  </div>
                  <Button
                    variant="danger"
                    leftIcon={<XCircleIcon className="w-5 h-5" />}
                    disabled={!rejectionReason.trim()}
                    isLoading={rejectKyc.isPending}
                    onClick={() => rejectKyc.mutate(rejectionReason.trim())}
                  >
                    Reject KYC
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* The customer's last rejection, once they have been sent away and
              before they come back. Kept visible so the broker remembers what
              they asked for. */}
          {isBrokerAdmin && quote.status === 'kyc_rejected' && kyc?.rejection_reason && (
            <Card>
              <CardHeader
                title="KYC Rejected"
                subtitle="The customer has been asked to send new documents."
              />
              <p className="text-sm text-gray-700 dark:text-gray-300">{kyc.rejection_reason}</p>
            </Card>
          )}

          {/* D4: payment and issuance. THE GATE IS ON THE SERVER -- it refuses
              both before `kyc_approved` -- so this card is presentation only.
              Do not move the rule up here; a hidden button is not a permission. */}
          {isBrokerAdmin && canUploadPolicy && (
            <Card>
              <CardHeader
                title="Payment & Policy Issuance"
                subtitle="The customer's KYC is approved. Record what they paid, then upload the issued policy."
              />
              <div className="space-y-5">
                {canRecordPayment && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                    <Input
                      label="Payment Reference"
                      name="payment_reference"
                      placeholder="UTR or transaction reference"
                      value={payment.payment_reference}
                      onChange={(e) =>
                        setPayment((p) => ({ ...p, payment_reference: e.target.value }))
                      }
                    />
                    <Input
                      label="Payment Date"
                      name="payment_date"
                      type="date"
                      value={payment.payment_date}
                      onChange={(e) => setPayment((p) => ({ ...p, payment_date: e.target.value }))}
                    />
                    <Select
                      label="Paid By"
                      options={PAYMENT_MODE_OPTIONS}
                      value={payment.payment_mode}
                      onChange={(e) => setPayment((p) => ({ ...p, payment_mode: e.target.value }))}
                    />
                    <div className="sm:col-span-3">
                      <Button
                        variant="outline"
                        isLoading={recordPayment.isPending}
                        disabled={!payment.payment_reference.trim() || !payment.payment_date}
                        onClick={() => recordPayment.mutate()}
                      >
                        Record Payment
                      </Button>
                    </div>
                  </div>
                )}

                <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
                  <input
                    ref={policyFileInputRef}
                    type="file"
                    accept="application/pdf,image/*"
                    className="hidden"
                    onChange={handlePolicyFilePicked}
                  />
                  <Button
                    leftIcon={<ArrowUpTrayIcon className="w-5 h-5" />}
                    onClick={() => policyFileInputRef.current?.click()}
                    isLoading={uploadDoc.isPending}
                  >
                    {policyDocuments.length > 0 ? 'Upload a Corrected Policy' : 'Upload Issued Policy'}
                  </Button>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Uploading the policy completes the quote and lets the customer download their
                    copy from the shared link.
                  </p>

                  {policyDocuments.length > 0 && (
                    <ul className="mt-3 divide-y divide-gray-100 dark:divide-gray-700">
                      {policyDocuments.map((doc) => (
                        <li key={doc.id} className="py-3 flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <PaperClipIcon className="w-5 h-5 text-gray-400 shrink-0" />
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{doc.name}</p>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => downloadDoc(doc)}>
                            Download
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* Attachments shown to the customer on the shared public link.
              Only quote_pdf and comparison_pdf are ever exposed publicly --
              the same model also holds KYC and ID proof, which the server
              filters out of the public payload and refuses to serve there. */}
          {isBrokerAdmin && (
            <Card>
              <CardHeader
                title="Quote Attachments"
                subtitle="Shown to the customer on the shared quote link"
              />
              <div className="space-y-4">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="w-56">
                    <Select
                      label="Document Type"
                      options={[
                        { value: 'quote_pdf', label: 'Quote PDF' },
                        { value: 'comparison_pdf', label: 'Comparison PDF' },
                      ]}
                      value={docType}
                      onChange={(e) => setDocType(e.target.value)}
                    />
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf,image/*"
                    className="hidden"
                    onChange={handleFilePicked}
                  />
                  <Button
                    variant="outline"
                    leftIcon={<ArrowUpTrayIcon className="w-5 h-5" />}
                    onClick={() => fileInputRef.current?.click()}
                    isLoading={uploadDoc.isPending}
                  >
                    Upload
                  </Button>
                </div>

                {attachments.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No attachment yet. Upload the insurer&apos;s quote PDF so the customer can
                    download it from the shared link.
                  </p>
                ) : (
                  <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                    {attachments.map((doc) => (
                      <li key={doc.id} className="py-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <PaperClipIcon className="w-5 h-5 text-gray-400 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{doc.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{doc.document_type_display}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => deleteDoc.mutate(doc.id)}
                          className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 shrink-0"
                          aria-label={`Remove ${doc.name}`}
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Card>
          )}

          {/* BOARD #278: the vehicle. Every field here was already on the wire
              and none of it was on the page, so a broker could not check the
              registration number, the variant or the IDV the quote was priced
              against without opening the edit form. */}
          {hasVehicle && (
            <Card>
              <CardHeader
                title="Vehicle"
                subtitle="What this quote is priced against. The insurer rates on the registration authority, the age and the IDV."
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <InfoItem
                  label="Registration Number"
                  value={vehicleInfo.registration_number || vehicleData.registration_number || '-'}
                />
                <InfoItem label="Vehicle" value={vehicleDescription || '-'} />
                <InfoItem
                  label="Registration Date"
                  value={
                    vehicleInfo.registration_date
                      ? formatDate(vehicleInfo.registration_date)
                      : vehicleData.registration_date
                      ? formatDate(vehicleData.registration_date)
                      : '-'
                  }
                />
                <InfoItem
                  label="Manufacturing Year"
                  value={
                    vehicleInfo.manufacturing_year ?? vehicleData.manufacturing_year ?? '-'
                  }
                />
                <InfoItem
                  label="RTO"
                  value={vehicleInfo.rto_name || vehicleInfo.rto_code || vehicleData.rto_code || '-'}
                />
                <InfoItem label="Fuel" value={vehicleData.fuel_type || '-'} />
                <InfoItem label="Cover" value={vehicleInfo.cover_type ? titleCase(vehicleInfo.cover_type.replace(/_/g, ' ')) : '-'} />
                {/* IDV is the sum insured the own-damage premium is a percentage
                    of, so it belongs beside the premium, not behind an edit form. */}
                <InfoItem
                  label="IDV"
                  value={
                    vehicleInfo.vehicle_idv ?? vehicleData.idv
                      ? `₹ ${vehicleInfo.vehicle_idv ?? vehicleData.idv}`
                      : '-'
                  }
                />
                <InfoItem label="Engine Number" value={vehicleData.engine_number || '-'} />
                <InfoItem label="Chassis Number" value={vehicleData.chassis_number || '-'} />
                <InfoItem
                  label="Financed"
                  value={
                    vehicleInfo.is_vehicle_financed || vehicleData.is_financed
                      ? vehicleInfo.financier_name || vehicleData.financier_name || 'Yes'
                      : 'No'
                  }
                />
              </div>
            </Card>
          )}

          {/* 24 Sep: who or what a health / travel request covers. */}
          <QuoteCoverDetailsCard health={extra.health_data} travel={extra.travel_data} />

          {/* BOARD #278: how this quote reached us. Only publicly originated
              quotes carry the block, so nothing is rendered for the ordinary
              broker-entered ones. */}
          {publicOrigin && (
            <Card>
              <CardHeader
                title="How this quote came in"
                subtitle="Raised from the public website, not by your staff."
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <InfoItem
                  label="Started"
                  value={publicOrigin.originated_at ? formatDate(publicOrigin.originated_at) : '-'}
                />
                <InfoItem label="Site" value={publicOrigin.site_slug || '-'} />
                <InfoItem
                  label="Lead"
                  value={
                    // A plain Link rather than `EntityLink`: that component's
                    // `EntityKind` union has no 'lead' member and it lives in
                    // components/common, outside this change's file domain.
                    publicOrigin.lead_id && publicOrigin.lead_number ? (
                      <Link
                        to={`/leads/${publicOrigin.lead_id}`}
                        className="text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        {publicOrigin.lead_number}
                      </Link>
                    ) : (
                      publicOrigin.lead_number || '-'
                    )
                  }
                />
                <InfoItem label="Address given" value={extra.address || '-'} />
              </div>
              {/* Said out loud rather than left to be assumed. There is no
                  delivery channel to send a one-time code on this deployment
                  (board #259), so nobody has checked this number -- and someone
                  about to ring a stranger should know that. */}
              {publicOrigin.phone_verified !== true && (
                <p className="mt-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-900 dark:text-amber-200">
                  This phone number has <strong>not</strong> been verified. No one-time code was
                  sent, so confirm who you are speaking to before issuing anything.
                </p>
              )}
            </Card>
          )}

          {/* Previous Policy Info (for renewals) */}
          {quote.business_type !== 'new' && (
            <Card>
              <CardHeader title="Previous Policy Information" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <InfoItem
                  label="Previous Policy Number"
                  value={quote.previous_policy_number || '-'}
                />
                {/* `previous_insurer` is the raw FK id -- rendering it printed a
                    UUID at the customer. QuoteRequestDetailSerializer resolves
                    the display name as `previous_insurer_name`; fall back to
                    nothing rather than back to the id. */}
                <InfoItem label="Previous Insurer" value={previousInsurerName || '-'} />
                <InfoItem
                  label="Previous Policy Expiry"
                  value={
                    quote.previous_policy_expiry
                      ? formatDate(quote.previous_policy_expiry)
                      : '-'
                  }
                />
                <InfoItem label="Previous NCB" value={previousNcb} />
                <InfoItem
                  label="Claims in Previous Policy"
                  value={quote.has_claim_in_previous ? 'Yes' : 'No'}
                />
              </div>
            </Card>
          )}

          {/* Timeline */}
          <Card>
            <CardHeader title="Quote Timeline" />
            <div className="space-y-4">
              <TimelineItem
                icon={<ClockIcon className="w-4 h-4" />}
                title="Quote Created"
                description={formatRelativeTime(quote.created_at)}
                time={formatDateTime(quote.created_at)}
                isCompleted
              />
              {quote.status !== 'draft' && (
                <TimelineItem
                  icon={<CheckCircleIcon className="w-4 h-4" />}
                  title="Quote Submitted"
                  description="Quote submitted for processing"
                  isCompleted={['submitted', 'processing', 'completed'].includes(quote.status)}
                />
              )}
              {['processing', 'completed'].includes(quote.status) && (
                <TimelineItem
                  icon={<ClockIcon className="w-4 h-4" />}
                  title="Processing"
                  description="Quote is being processed"
                  isCompleted={quote.status === 'completed'}
                />
              )}
              {quote.status === 'completed' && (
                <TimelineItem
                  icon={<CheckCircleIcon className="w-4 h-4" />}
                  title="Quote Completed"
                  description="Quote comparison ready"
                  isCompleted
                />
              )}
              {['cancelled', 'expired'].includes(quote.status) && (
                <TimelineItem
                  icon={<XCircleIcon className="w-4 h-4" />}
                  title={quote.status === 'cancelled' ? 'Cancelled' : 'Expired'}
                  description={`Quote ${quote.status}`}
                  isCompleted
                  isError
                />
              )}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Card */}
          <Card>
            <CardHeader title="Status" />
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Quote Status</span>
                <Badge variant={getStatusBadgeVariant(quote.status)}>
                  {statusLabel(quote.status)}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Business Type</span>
                <Badge variant={quote.business_type === 'new' ? 'primary' : 'info'}>
                  {titleCase(quote.business_type)}
                </Badge>
              </div>
            </div>
          </Card>

          {/* A6: the "Dates" card that used to sit here said the same two
              things as the shared RecordTimestamps footer below, but in two
              other formats (a bare date, and "2 weeks ago"). Dropped in favour
              of the footer so all four record screens read identically. */}

          {/* BROKER-7: the Broker Admin drives the quote through its whole
              lifecycle. Hidden from POSPs, who see submitted quotes but do not
              own their status.

              The public link used to be minted here too. It is now on the
              premium options card -- one control instead of two, next to the
              options the customer is being sent, and the URL it returns is
              actually put on screen. See SharePanel there. */}
          {isBrokerAdmin && (
            <Card>
              <CardHeader title="Quote Status" />
              <div className="space-y-3">
                <Select
                  label="Status"
                  options={QUOTE_STATUS_OPTIONS}
                  value={quote.status}
                  onChange={(e) => updateStatus.mutate(e.target.value)}
                  disabled={updateStatus.isPending}
                />
              </div>
            </Card>
          )}

          {/* Quick Actions */}
          {(canCreateQuote || quote.customer_profile_id) && (
          <Card>
            <CardHeader title="Quick Actions" />
            <div className="space-y-3">
              {canCreateQuote && (
                <Link to={`/quotes/new?duplicate=${id}`} className="block">
                  <Button variant="outline" className="w-full justify-start">
                    Duplicate Quote
                  </Button>
                </Link>
              )}
              {/* BROKER-3: link by the customers.Customer row id, not the
                  users.User id on the quote -- the latter always 404'd. */}
              {quote.customer_profile_id && (
                <Link to={`/customers/${quote.customer_profile_id}`} className="block">
                  <Button variant="outline" className="w-full justify-start">
                    View Customer
                  </Button>
                </Link>
              )}
            </div>
          </Card>
          )}
        </div>
      </div>

      {/* A6: record audit footer, below both columns. */}
      <RecordTimestamps createdAt={quote.created_at} updatedAt={quote.updated_at} />
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-900 dark:text-white mt-0.5">{value}</p>
    </div>
  );
}

interface TimelineItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  time?: string;
  isCompleted: boolean;
  isError?: boolean;
}

function TimelineItem({ icon, title, description, time, isCompleted, isError }: TimelineItemProps) {
  return (
    <div className="flex gap-3">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center ${
          isError
            ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
            : isCompleted
            ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
        }`}
      >
        {icon}
      </div>
      <div className="flex-1 pb-4 border-l-2 border-gray-100 dark:border-gray-700 pl-4 -ml-4">
        <p className="text-sm font-medium text-gray-900 dark:text-white">{title}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
        {time && <p className="text-xs text-gray-400 mt-1">{time}</p>}
      </div>
    </div>
  );
}
